import { createEffect, createSignal, on, type Accessor } from "solid-js"
import {
  Group, Mesh, MeshBasicMaterial, PlaneGeometry,
  Raycaster, type Object3D, type Sprite
} from "three"
import type { Nullable } from "vite-node"
import { dijkstraDirections } from "../../../../lib/core/direction/dijkstraDirections"
import type { Engine } from "../../../../lib/core/engine/Engine.type"
import { directionDxDyToEntityHeading } from "../../../../lib/core/entity/EntityHeading/directionDxDyToEntityHeading"
import type { EntityTypeId } from "../../../../lib/core/entity/EntityTypeId.enum"
import type { EntityTypeMeta } from "../../../../lib/core/EntityTypeMeta"
import { createRownipulatorAddRowAction } from "../../../../lib/core/state/flux/action/Map/RownipulatorAddRowAction"
import dispatchClient from "../../../../lib/core/state/flux/dispatchClient"
import { HALF_PI } from "../../../../lib/core/util/math/Math"
import firstIfOnlyOne from "../../../../lib/ldsh/firstIfOnlyOne"
import identity from "../../../../lib/ldsh/identity"
import onElementEvent from "../../../../lib/onElementEvent"
import { VERTICES_PER_TILE } from "../../../../lib/three/consts"
import useMainScene from "../../../../lib/three/fn/useMainScene"
import usePointerRefVector2 from "../../../../lib/three/fn/usePointerRefVector2"
import createCenteredTextSprite from "../../../../lib/three/sprite/createCenteredTextSprite"
import { onCleanupRemoveAndDispose } from "../../../../rx/cleanup/onCleanupRemoveAndDispose"
import { getEngineThreeMainCamera } from "../../../../lib/three/fn/getSetEngineThreeMainCamera"
import { getEngineThreeMainRenderer } from "../../../../lib/three/fn/getSetEngineThreeMainRenderer"

/**
 * createAddRowGroup
 *
 * Adds a group of directionally positioned "Add Row" sprites and interactive overlays to the 3D scene.
 * These UI elements allow users to click to extend the grid in a given direction.
 *
 * @param engine - The core Engine context that holds Three.js and game state.
 * @param getSelectedLayer1Etm - Signal accessor for the currently selected terrain type.
 * @param interactiveObjects - An array where interactive 3D objects are registered for global raycasting.
 */
export default function createAddRowGroup(
  engine: Engine,
  getSelectedLayer1Etm: Accessor<Nullable<EntityTypeMeta>>,
  interactiveObjects: Object3D[],
) {
  // Track currently hovered sprite (for hover effects)
  const [getHoveredSprite, setHoveredSprite] = createSignal<Nullable<Sprite>>(null)
  const pointerRef = usePointerRefVector2(engine)

  const scene = useMainScene(engine)
  const renderer = getEngineThreeMainRenderer(engine)
  const camera = getEngineThreeMainCamera(engine)

  // Group to hold all interactive "Add Row" UI elements
  const group = new Group()
  onCleanupRemoveAndDispose(group) // ensure group is disposed on cleanup
  scene.add(group)

  // Create one sprite + interaction plane for each grid direction (top, bottom, left, right)
  dijkstraDirections.forEach((direction) => {
    const { dx, dy } = direction
    const rh = 30     // Y position for the sprite
    const planeY = 8  // Y position for the clickable plane

    // Create text sprite
    const sprite = createCenteredTextSprite('Add Row', 30, 0.1)

    // Create invisible clickable plane behind the sprite
    const planeGeometry = new PlaneGeometry(VERTICES_PER_TILE, VERTICES_PER_TILE)
    planeGeometry.rotateX(-HALF_PI) // Make it horizontal
    const planeMaterial = new MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.5,
    })
    const planeMesh = new Mesh(planeGeometry, planeMaterial)

    // Add both to the group
    group.add(sprite)
    group.add(planeMesh)

    // Make sprite interactive via raycasting
    interactiveObjects.push(sprite)

    // Reactively update position based on grid size changes
    createEffect(() => {
      const { width, height } = engine.state
      const halfTile = VERTICES_PER_TILE / 2
      const offsetX = (width - 1) * halfTile
      const offsetZ = (height - 1) * halfTile

      // Position sprite and plane depending on direction
      if (dy !== 0) {
        // Vertical (north/south)
        sprite.position.set(
          offsetX,
          rh,
          dy * VERTICES_PER_TILE * ((height / 2) + 0.5) + offsetZ
        )
        planeMesh.position.set(
          offsetX,
          planeY,
          dy * VERTICES_PER_TILE * ((height / 2) + 0.5) + offsetZ
        )
        planeMesh.scale.x = dy * width // stretch plane to match row width
      } else {
        // Horizontal (east/west)
        sprite.position.set(
          dx * VERTICES_PER_TILE * ((width / 2) + 0.5) + offsetX,
          rh,
          offsetZ
        )
        planeMesh.position.set(
          dx * VERTICES_PER_TILE * ((width / 2) + 0.5) + offsetX,
          planeY,
          offsetZ
        )
        planeMesh.scale.z = dx * height // stretch plane to match column height
      }
    })

    // Visual hover effect: highlight sprite and plane when hovered
    createEffect(on(getHoveredSprite, (hoveredSprite) => {
      if (hoveredSprite === sprite) {
        hoveredSprite.material.color.set(0x00ff00)
        planeMaterial.opacity = 0.6
      } else {
        sprite.material.color.set(0xffffff)
        planeMaterial.opacity = 0.2
      }
    }))

    // Click handler for sprite
    sprite.userData.onClick = () => {
      const entityTypeIdList: Array<EntityTypeId> = [getSelectedLayer1Etm()?.id].filter(identity) as Array<EntityTypeId>
      console.log('onClick', sprite, { dx, dy })
      dispatchClient(engine, createRownipulatorAddRowAction(
        entityTypeIdList,
        directionDxDyToEntityHeading(direction)
      ))
    }
  })

  // Setup raycaster and event listeners
  const raycaster = new Raycaster()
  const { domElement } = renderer

  // Handle pointer movement for hover detection
  onElementEvent(domElement, 'pointermove', function onPointerMove() {
    raycaster.setFromCamera(pointerRef, camera)
    const intersects = raycaster.intersectObjects(interactiveObjects)
    const intersection = firstIfOnlyOne(intersects)
    setHoveredSprite(intersection?.object as Nullable<Sprite>)
  })

  // Handle click events on interactive objects
  onElementEvent(domElement, 'click', function onClick() {
    raycaster.setFromCamera(pointerRef, camera)
    const intersects = raycaster.intersectObjects(interactiveObjects)
    const intersection = firstIfOnlyOne(intersects)
    if (intersection) {
      const clicked = intersection.object
      clicked.userData.onClick?.()
    }
  })
}
